import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, UserProfile } from '../types';
import { fetchUserProfile, updateUserProfile, getCurrentUser, supabase } from '../services/backendService';
import logger from "@/utils/logger";
import {
    Save, Lock, HeartPulse, Activity, AlertCircle, FileText, Leaf, ShieldCheck,
    UserCircle, Ruler, Weight, Target, Info, Droplet, Moon, Sun, RefreshCw,
    Utensils, Pill, Upload, HelpCircle, Zap, ChevronDown, ChevronUp, MoreHorizontal
} from 'lucide-react';
import DoshaIllustration from './DoshaIllustration';

interface WellnessPassportProps {
    onBack: () => void;
}

// Wisdom Tooltip Component
const WisdomTooltip: React.FC<{ text: string }> = ({ text }) => (
    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-40 bg-[#2D5A27]/95 text-[#F5F5DC] text-[10px] leading-relaxed font-medium p-3 rounded-xl shadow-xl opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-[100] backdrop-blur-sm">
        <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-[#2D5A27]/95" />
        {text}
    </div>
);

// Wave Dial Component
const WaveDial: React.FC<{ value: number, onChange: (val: number) => void, label: string, icon: React.ReactNode, minLabel: string, maxLabel: string }> = ({ value, onChange, label, icon, minLabel, maxLabel }) => {
    return (
        <div className="space-y-4">
            <label className="flex items-center justify-between text-xs font-bold text-[#2D5A27] uppercase tracking-wider">
                <span className="flex items-center gap-2">{icon} {label}</span>
                <span className="text-[#2D5A27]/60">{value}/10</span>
            </label>
            <div className="h-16 flex items-end justify-between gap-1 px-2">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((level) => {
                    const isActive = value >= level;
                    const isSelected = value === level;
                    // Calculate height based on level to create a wave effect
                    // Center (5-6) is higher? Or just linear?
                    // Let's do a "wave" where intensity correlates to height + color
                    const heightPercent = 30 + (level * 7);

                    return (
                        <button
                            key={level}
                            onClick={() => onChange(level)}
                            className={`w-full rounded-t-md transition-all duration-300 relative group
                                ${isActive ? 'bg-[#2D5A27] opacity-80' : 'bg-[#2D5A27]/10'}
                                ${isSelected ? '!bg-[#C084FC] !opacity-100 shadow-[0_0_15px_rgba(192,132,252,0.5)]' : ''}
                            `}
                            style={{ height: `${heightPercent}%` }}
                        >
                            {isSelected && <div className="absolute -top-6 left-1/2 -translate-x-1/2 text-[10px] font-bold text-[#2D5A27]">{level}</div>}
                        </button>
                    )
                })}
            </div>
            <div className="flex justify-between text-[10px] text-gray-400 font-bold tracking-widest uppercase">
                <span>{minLabel}</span>
                <span>{maxLabel}</span>
            </div>
        </div>
    );
};

// Dosha Colors
const DOSHA_COLORS: Record<string, string> = {
    vata: '#C084FC',
    pitta: '#F87171',
    kapha: '#4ADE80',
    default: '#2D5A27'
};

const DOSHA_INFO: Record<string, any> = {
    vata: {
        icon: '🍃',
        elements: 'Ether + Air',
        controls: 'Cell division, breathing, heartbeat, mind',
        balanced: 'Creative, energetic, adaptable',
        imbalance: 'Anxiety, dry skin, insomnia, arthritis',
        aggravatedBy: 'Fear, grief, dry foods, late nights, cold',
        description: 'Ayurveda describes Vata as the most powerful dosha, governing the most fundamental body processes.'
    },
    pitta: {
        icon: '🔥',
        elements: 'Fire + Water',
        controls: 'Hormones, digestion, metabolism',
        balanced: 'Sharp, focused, confident',
        imbalance: 'Anger, heartburn, inflammation, hypertension',
        aggravatedBy: 'Spicy food, too much sun, fatigue, alcohol',
        description: 'Pitta governs all heat, metabolism and transformation in the mind and body.'
    },
    kapha: {
        icon: '🌊',
        elements: 'Water + Earth',
        controls: 'Growth, strength, immunity',
        balanced: 'Calm, loving, grounded',
        imbalance: 'Weight gain, lethargy, congestion, diabetes risk',
        aggravatedBy: 'Oversleeping, sweet foods, daytime naps',
        description: 'Kapha provides structure, strength, and stability to both body and mind.'
    }
};

const getDoshaElements = (dosha?: string) => {
    if (!dosha) return "";
    const elementsMapping: Record<string, string[]> = {
        vata: ['Ether', 'Air'],
        pitta: ['Fire', 'Water'],
        kapha: ['Water', 'Earth']
    };
    const parts = dosha.toLowerCase().split('-');
    const allElements = parts.flatMap(p => elementsMapping[p] || []);
    const uniqueElements = Array.from(new Set(allElements));
    return uniqueElements.join(' + ');
};

interface SnapshotSection {
    label: string;
    fields: Record<string, any>;
}

const SnapshotCard: React.FC<{ title: string, icon: React.ReactNode, sections: SnapshotSection[], color: string }> = ({ title, icon, sections, color }) => (
    <div className="bg-white border border-gray-100 rounded-[1.2rem] p-6 shadow-sm">
        <div className="flex items-center gap-3 mb-4">
            <div className={`p-2 rounded-lg bg-${color}/10 text-${color}`}>
                {icon}
            </div>
            <h4 className="text-[13px] font-bold text-[#1A3818] uppercase tracking-wider">{title}</h4>
        </div>
        {sections.map((section, sIdx) => (
            <div key={sIdx} className="mb-6 last:mb-0">
                <h5 className="text-[10px] uppercase tracking-[0.08em] text-gray-400 font-black mt-4 mb-3 border-b border-gray-100 pb-1">
                    {section.label}
                </h5>
                <div className="grid grid-cols-1 gap-y-3">
                    {Object.entries(section.fields).map(([label, value]) => {
                        let displayValue = '—';
                        if (value !== undefined && value !== null && value !== '') {
                            if (Array.isArray(value)) {
                                displayValue = value.length > 0 ? value.join(', ') : 'None noted';
                            } else {
                                displayValue = value.toString();
                            }
                        } else if (label.toLowerCase().includes('allergies') || label.toLowerCase().includes('medications') || label.toLowerCase().includes('history')) {
                            displayValue = 'None noted';
                        }

                        return (
                            <div key={label} className="flex flex-col border-b border-gray-50 pb-2 last:border-0 last:pb-0">
                                <span className="text-[10px] text-gray-400 font-bold uppercase tracking-widest mb-1 leading-none">{label}</span>
                                <span className="text-[13px] text-[#1A3818] font-medium capitalize">{displayValue}</span>
                            </div>
                        );
                    })}
                </div>
            </div>
        ))}
    </div>
);

const WellnessPassport: React.FC<WellnessPassportProps> = ({ onBack }) => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [profile, setProfile] = useState<Partial<UserProfile>>({});
    const [onboardingProfile, setOnboardingProfile] = useState<any>(null);
    const [journey, setJourney] = useState<any>(null);
    const [currentUser, setCurrentUser] = useState<User | null>(null);
    const [isDoshaExpanded, setIsDoshaExpanded] = useState(false);

    // UI State for inputs
    const [allergyInput, setAllergyInput] = useState('');
    const [medicationInput, setMedicationInput] = useState('');
    const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
    const [ageError, setAgeError] = useState<string | null>(null);

    // Common options
    const goals = ["Manage Chronic Pain", "Better Digestion", "Stress Relief", "Weight Balance", "More Energy", "Hormonal Health"];
    const diets = ["Vegetarian", "Vegan", "Omnivore", "Paleo", "Keto", "Ayurvedic"];
    const sleepPatterns = ["Restful", "Trouble falling asleep", "Waking up often", "Early riser", "Insomnia"];
    const conditionsList = ["Diabetes", "Hypertension", "Asthma", "Acne", "Anxiety", "Depression", "IBS", "Arthritis"];

    useEffect(() => {
        const init = async () => {
            const user = getCurrentUser();
            if (user) {
                setCurrentUser(user);
                const data = await fetchUserProfile(user.id);
                if (data) {
                    // Unpack extended fields from health_data JSONB if present
                    const hd = data.health_data || {};
                    setProfile({
                        ...data,
                        health_data: hd,
                        allergies: data.allergies || [],
                        medications: data.medications || hd.medications || [],
                        // Prioritize top-level if exists (legacy), else check health_data
                        height: data.height || hd.height,
                        weight: data.weight || hd.weight,
                        primary_goal: data.primary_goal || hd.primary_goal,
                        dietary_preference: data.dietary_preference || hd.dietary_preference,
                        activity_level: data.activity_level || hd.activity_level,
                        sleep_pattern: data.sleep_pattern || hd.sleep_pattern,
                        stress_level: data.stress_level || hd.stress_level,
                        lab_metrics: data.lab_metrics || hd.lab_metrics || {}
                    });
                } else {
                    setProfile({
                        health_data: {},
                        allergies: [],
                        medications: []
                    });
                }

                // Fetch Onboarding Data
                const { data: onboarding } = await supabase
                    .from('onboarding_profiles')
                    .select('*')
                    .eq('user_id', user.id)
                    .maybeSingle();

                if (onboarding) {
                    setOnboardingProfile(onboarding);
                }

                // Fetch Journey Data
                const { data: journeyData } = await supabase
                    .from('user_journey')
                    .select('*')
                    .eq('user_id', user.id)
                    .maybeSingle();

                if (journeyData) {
                    setJourney(journeyData);
                }
            }
            setLoading(false);
        };
        init();
    }, []);

    const handleSave = async () => {
        if (!currentUser) {
            setMessage({ type: 'error', text: 'You must be logged in to save.' });
            return;
        }

        // Validate age immediately before saving
        if (profile.age !== undefined && profile.age !== null && profile.age.toString() !== '') {
            if (profile.age < 13) {
                setAgeError('You must be 13 or older to use NatureNani.');
                setMessage({ type: 'error', text: 'Please resolve the errors before saving.' });
                return;
            } else if (profile.age > 120) {
                setAgeError('Please enter a valid age.');
                setMessage({ type: 'error', text: 'Please resolve the errors before saving.' });
                return;
            }
        }

        if (ageError) {
            setMessage({ type: 'error', text: 'Please resolve the errors before saving.' });
            return;
        }
        setSaving(true);
        setMessage(null);
        try {
            // Pack extended fields into health_data JSONB to avoid schema changes
            const {
                height, weight, primary_goal, dietary_preference,
                activity_level, sleep_pattern, stress_level, lab_metrics,
                medications,
                health_data, ...baseProfile
            } = profile;

            const packedHealthData = {
                ...health_data,
                // Pack the new fields
                height, weight, primary_goal, dietary_preference,
                activity_level, sleep_pattern, stress_level, lab_metrics,
                medications,
                // Ensure medical_history is preserved
                medical_history: health_data?.medical_history
            };

            const dataToSave = {
                id: currentUser.id,
                // Only include fields that actually exist in the DB schema at top level
                age: baseProfile.age,
                sex: baseProfile.sex,
                allergies: baseProfile.allergies,
                // Medications moved to health_data
                // Everything else goes into health_data
                health_data: packedHealthData,
                updated_at: new Date().toISOString()
            };

            const updated = await updateUserProfile(currentUser.id, dataToSave);
            if (updated) {
                // Update local state by merging the saved data back with the unpacked structure
                const hd = updated.health_data || {};
                setProfile({
                    ...updated,
                    height: hd.height,
                    weight: hd.weight,
                    primary_goal: hd.primary_goal,
                    dietary_preference: hd.dietary_preference,
                    activity_level: hd.activity_level,
                    sleep_pattern: hd.sleep_pattern,
                    stress_level: hd.stress_level,
                    lab_metrics: hd.lab_metrics || {},
                    health_data: hd
                });
                setMessage({ type: 'success', text: 'Passport synced. Nani has updated your blueprint.' });
                setTimeout(() => setMessage(null), 3000);
            } else {
                setMessage({ type: 'error', text: 'Failed to sync blueprint.' });
            }
        } catch (e) {
            logger.error(e);
            setMessage({ type: 'error', text: 'An error occurred during sync.' });
        } finally {
            setSaving(false);
        }
    };

    // Helper to update simple fields
    const updateField = (field: keyof UserProfile, value: any) => {
        setProfile(prev => ({ ...prev, [field]: value }));
    };

    const handleAgeBlur = () => {
        if (profile.age !== undefined && profile.age !== null && profile.age.toString() !== '') {
            if (profile.age < 13) {
                setAgeError('You must be 13 or older to use NatureNani.');
            } else if (profile.age > 120) {
                setAgeError('Please enter a valid age.');
            } else {
                setAgeError(null);
            }
        } else {
            setAgeError(null);
        }
    };

    // Helper for nested metric updates
    const updateMetric = (key: string, value: string) => {
        setProfile(prev => ({
            ...prev,
            lab_metrics: { ...prev.lab_metrics, [key]: value }
        }));
    };

    // Array helpers
    const addArrayItem = (field: 'allergies' | 'medications', value: string, setter: (s: string) => void) => {
        if (value.trim()) {
            const current = profile[field] || [];
            if (!current.includes(value.trim())) {
                setProfile(prev => ({ ...prev, [field]: [...(prev[field] || []), value.trim()] }));
            }
            setter('');
        }
    };

    const removeArrayItem = (field: 'allergies' | 'medications', item: string) => {
        setProfile(prev => ({ ...prev, [field]: (prev[field] || []).filter(i => i !== item) }));
    };

    const toggleCondition = (condition: string) => {
        const currentHistory = Array.isArray(profile.health_data?.medical_history)
            ? profile.health_data.medical_history
            : (profile.health_data?.medical_history ? [profile.health_data.medical_history as string] : []);

        const exists = currentHistory.includes(condition);
        const newHistory = exists
            ? currentHistory.filter(c => c !== condition)
            : [...currentHistory, condition];

        setProfile(prev => ({
            ...prev,
            health_data: { ...prev.health_data, medical_history: newHistory }
        }));
    };

    if (loading) return <div className="flex items-center justify-center h-full min-h-[600px]"><Leaf className="animate-spin text-[#2D5A27]" /></div>;

    const calculateCompletion = () => {
        if (!profile) return 0;
        let total = 0;
        
        // S0: Essential Details (Passport) - Weighted to 40%
        let s0 = 0;
        if (profile.age && profile.sex) s0 += 10;
        if (profile.height && profile.weight) s0 += 10;
        if (profile.occupation) s0 += 5;
        if (profile.primary_goal) s0 += 5;
        if (profile.dietary_preference) s0 += 4;
        if (profile.health_data?.medical_history && Array.isArray(profile.health_data.medical_history) && profile.health_data.medical_history.length > 0) s0 += 2;
        if (profile.allergies && profile.allergies.length > 0) s0 += 2;
        if (profile.medications && profile.medications.length > 0) s0 += 2;
        total += Math.min(s0, 40); // Max 40%
        
        // S1: Vikriti (Imbalance Detection) - Weighted to 10%
        const isWellness = profile.user_intent === 'wellness';
        const s1Done = !!(profile.vikriti_dosha || (isWellness && onboardingProfile?.vikriti_skipped));
        if (s1Done) total += 10;
        
        // S2: Prakriti (Lifelong Nature) - Weighted to 50%
        // "If user skips Prakriti, profile completion shows 50% incomplete" 
        // This implies Prakriti is exactly 50%.
        if (profile.prakriti_dosha) total += 50;
        
        return Math.min(total, 100);
    };

    const completion = calculateCompletion();

        const hasPrakriti = !!profile.prakriti_dosha;
        const hasVikriti = !!profile.vikriti_dosha;
        const onboardingCompleted = journey?.onboarding_completed;

        let journeyState: 'A' | 'B' | 'C' = 'A';
        if (onboardingCompleted && hasPrakriti && hasVikriti) journeyState = 'C';
        else if (hasPrakriti && !hasVikriti) journeyState = 'B';
        else if (!onboardingCompleted) journeyState = 'A';

        return (
            <div className="h-full overflow-y-auto pb-20 bg-[#FFFCF7] selection:bg-[#D8F3DC]">
                <style>
                    {`
                        @keyframes shimmer {
                            0% { transform: translateX(-100%); }
                            100% { transform: translateX(100%); }
                        }
                        .animate-shimmer {
                            animation: shimmer 2s infinite;
                        }
                    `}
                </style>
                <div className="max-w-5xl mx-auto p-4 md:p-8 md:pt-2 space-y-6 my-1 relative">
                    {/* Subtle Nature Pattern Asset */}
                    <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M30 5c-5 10-15 15-15 25s10 15 15 25c5-10 15-15 15-25s-10-15-15-25z' fill='%232D5A27' fill-opacity='0.4' fill-rule='evenodd'/%3E%3C/svg%3E")`, backgroundSize: '120px 120px' }} />

                    {/* 1. Header: The Warm Welcome */}
                    <header className="relative mb-4 md:mb-6">
                        <div className="relative py-4 px-4 md:py-6 md:px-8">
                            <div className="flex items-center justify-between relative z-10 max-w-5xl mx-auto mb-6">
                                <div className="w-6" /> {/* Spacer for centering */}
                                <h2 className="text-[11px] font-bold tracking-[0.2em] text-[#1A3818]/60 uppercase text-center">
                                    Your Ayurvedic Blueprint
                                </h2>
                                <button className="text-[#1A3818]/40 hover:text-[#1A3818] transition-colors">
                                    <MoreHorizontal size={20} />
                                </button>
                            </div>

                            <div className="flex-1 w-full flex flex-col md:flex-row gap-6 justify-center max-w-5xl mx-auto">
                                {/* Prakriti Card */}
                                <div className="flex-1 max-w-[420px] bg-white rounded-[1.5rem] border border-gray-100 shadow-sm overflow-hidden flex flex-col">
                                    <div className="bg-[#1e4d38] p-6 relative overflow-hidden shrink-0">
                                        <div className="relative z-10">
                                            <span className="inline-block px-3 py-1 rounded-full bg-white/10 text-white/80 text-[10px] font-bold uppercase tracking-widest mb-4">
                                                {profile.prakriti_dosha === 'sama' ? "The rarest constitutional type" : "Lifelong nature (Prakriti)"}
                                            </span>
                                            <div className="flex items-end justify-between">
                                                <div className="flex flex-col">
                                                    {journeyState === 'A' ? (
                                                        <>
                                                            <h3 className="text-[22px] font-bold text-white/50 mb-1">Your Nature</h3>
                                                            <p className="text-[11px] text-white/40 font-black uppercase tracking-[0.15em]">
                                                                Discover your lifelong constitution
                                                            </p>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <h3 className="text-4xl font-bold text-white mb-1">
                                                                {profile.prakriti_dosha ? (profile.prakriti_dosha === 'sama' ? 'Sama' : profile.prakriti_dosha.split('-').map(s => s.charAt(0).toUpperCase() + s.slice(1)).join('-')) : '—'}
                                                            </h3>
                                                            <p className="text-[10px] text-white/60 font-black uppercase tracking-[0.15em]">
                                                                {getDoshaElements(profile.prakriti_dosha)}
                                                            </p>
                                                        </>
                                                    )}
                                                </div>
                                                {journeyState !== 'A' && profile.prakriti_dosha && (
                                                    <div className="opacity-90">
                                                        <DoshaIllustration dosha={profile.prakriti_dosha as any} />
                                                    </div>
                                                )}
                                            </div>
                                            {journeyState !== 'A' && (
                                                <p className="mt-6 text-[11px] text-white/90 leading-relaxed font-medium">
                                                    {profile.prakriti_dosha === 'sama' 
                                                        ? "In the Charaka Samhita, Sama Prakriti is described as the ideal constitution — one where Vata, Pitta, and Kapha exist in natural harmony. You carry the creativity of Vata, the focus of Pitta, and the steadiness of Kapha in equal measure."
                                                        : "Your Dosha blueprint, set at conception. This is your healthy baseline — constant throughout your life."}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                    <div className="p-6 pt-5 bg-white flex-1 flex flex-col">
                                        {journeyState === 'A' ? (
                                            <button 
                                                onClick={() => navigate('/onboarding')}
                                                className="w-full py-4 px-4 bg-white text-[#2D6A4F] text-[13px] font-bold rounded-xl transition-all flex items-center justify-center gap-2 border border-[#2D6A4F]/20 hover:bg-[#f0f9f1]"
                                            >
                                                Begin your wellness journey →
                                            </button>
                                        ) : (
                                            <>
                                                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-3">Balanced Traits</p>
                                                <div className="flex flex-wrap gap-2 mb-6">
                                                    {(profile.prakriti_dosha?.includes('vata') ? ['Creative', 'Energetic', 'Adaptable'] : 
                                                      profile.prakriti_dosha?.includes('pitta') ? ['Intelligent', 'Courageous', 'Leader'] : 
                                                      ['Calm', 'Steady', 'Loyal']).map(trait => (
                                                        <span key={trait} className="px-4 py-1.5 bg-[#f0f9f1] rounded-full text-[11px] font-bold text-[#2D6A4F]">
                                                            {trait}
                                                        </span>
                                                    ))}
                                                </div>
                                                <button 
                            onClick={() => navigate('/onboarding', { state: { mode: 'prakriti-retake' } })}
                            className="w-full bg-white/50 border border-[#2D6A4F]/20 text-[#2D6A4F] text-xs font-bold py-3 rounded-xl hover:bg-[#2D6A4F] hover:text-white transition-all shadow-sm"
                        >
                            Retake Assessment
                        </button>
                                            </>
                                        )}
                                    </div>
                                </div>

                                {/* Vikriti Card */}
                                <div className="flex-1 max-w-[420px] bg-white rounded-[1.5rem] border border-gray-100 shadow-sm overflow-hidden flex flex-col">
                                    <div className="bg-[#7c4a0f] p-6 relative overflow-hidden shrink-0">
                                        <div className="relative z-10">
                                            <span className="inline-block px-3 py-1 rounded-full bg-white/10 text-white/80 text-[10px] font-bold uppercase tracking-widest mb-4">
                                                Current state (Vikriti)
                                            </span>
                                            <div className="flex items-end justify-between">
                                                <div className="flex flex-col">
                                                    {journeyState === 'A' ? (
                                                        <>
                                                            <h3 className="text-[22px] font-bold text-white/50 mb-1">Your Current State</h3>
                                                            <p className="text-[11px] text-white/40 font-black uppercase tracking-[0.15em]">
                                                                Understand what's out of balance today
                                                            </p>
                                                        </>
                                                    ) : journeyState === 'B' ? (
                                                        <>
                                                            <h3 className="text-[18px] font-bold text-white/60 mb-1">Not yet assessed</h3>
                                                            <p className="text-[11px] text-white/40 font-black uppercase tracking-[0.15em]">
                                                                Add a current concern for targeted guidance
                                                            </p>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <h3 className="text-4xl font-bold text-white mb-1">
                                                                {profile.vikriti_dosha ? (profile.vikriti_dosha === 'sama' ? 'Sama' : profile.vikriti_dosha.split('-').map(s => s.charAt(0).toUpperCase() + s.slice(1)).join('-')) : '—'}
                                                            </h3>
                                                            <p className="text-[10px] text-white/60 font-black uppercase tracking-[0.15em]">
                                                                {getDoshaElements(profile.vikriti_dosha)}
                                                            </p>
                                                        </>
                                                    )}
                                                </div>
                                                {journeyState === 'C' && profile.vikriti_dosha && (
                                                    <div className="opacity-90">
                                                        <DoshaIllustration dosha={profile.vikriti_dosha as any} />
                                                    </div>
                                                )}
                                            </div>
                                            {journeyState === 'C' && (
                                                <p className="mt-6 text-[11px] text-white/90 leading-relaxed font-medium">
                                                    Your current state of health. Stress, diet, or seasons can push you out of balance — Nani helps bring this back.
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                    <div className="p-6 pt-5 bg-white flex-1 flex flex-col">
                                        {journeyState === 'A' ? (
                                            <div className="h-full" /> /* No button for Vikriti in State A */
                                        ) : journeyState === 'B' ? (
                                            <div className="flex flex-col h-full">
                                                <p className="text-[10px] font-medium text-amber-600 mb-4 bg-amber-50 p-3 rounded-lg border border-amber-100/50">
                                                    You haven't assessed your current state yet.
                                                </p>
                                                <button 
                                                    onClick={() => navigate('/onboarding', { state: { mode: 'vikriti-refresh' } })}
                                                    className="w-full bg-amber-600 text-white text-xs font-bold py-3 rounded-xl hover:bg-amber-700 transition-all shadow-sm"
                                                >
                                                    Evaluate current state
                                                </button>
                                            </div>
                                        ) : (
                                            <>
                                                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-3">Imbalance Indicators</p>
                                                <div className="flex flex-wrap gap-2 mb-6">
                                                    {(profile.health_data?.affected_zones ? Object.values(profile.health_data.affected_zones) : []).map((zone: any, idx: number) => (
                                                        <span key={idx} className="px-2 py-1 bg-amber-50 text-amber-700 text-[9px] font-black uppercase tracking-tighter rounded-md border border-amber-100/50">
                                                            {zone.zone}
                                                        </span>
                                                    ))}
                                                </div>
                                                <button 
                                                    onClick={() => navigate('/onboarding', { state: { mode: 'vikriti-refresh' } })}
                                                    className="w-full bg-white/50 border border-amber-200 text-amber-800 text-xs font-bold py-3 rounded-xl hover:bg-amber-600 hover:text-white transition-all shadow-sm"
                                                >
                                                    Update state
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Completion Bar - Bolder per screenshot */}
                            <div className="mt-8 group max-w-5xl mx-auto px-4">
                                <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2">
                            <h2 className="text-[10px] font-black uppercase tracking-[0.2em] text-[#1A3818]/40">Profile Completion</h2>
                            {completion < 100 && (
                                <button 
                                    onClick={() => navigate('/onboarding', { state: { mode: 'prakriti-retake', skipToIncomplete: true } })}
                                    className="text-[10px] font-bold text-[#2D6A4F] hover:underline"
                                >
                                    • Complete missing details
                                </button>
                            )}
                        </div>
                        <span className="text-sm font-serif font-black text-[#1A3818]">{completion}%</span>
                    </div>
                                <div className="h-2 bg-emerald-50 w-full rounded-full overflow-hidden">
                                    <div
                                        className="h-full bg-[#52B788] rounded-full transition-all duration-1000 ease-out"
                                        style={{ width: `${completion}%` }}
                                    />
                                </div>
                                
                                {completion < 100 && (
                                    <div className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-2">
                                        <span className="text-[9px] font-black text-[#1A3818]/30 uppercase tracking-[0.1em]">Missing To 100%</span>
                                        <div className="flex flex-wrap gap-2">
                                            {(!profile.age || !profile.sex) && <span className="text-[9px] bg-white text-[#1A3818]/60 px-2 py-1 rounded-lg border border-[#1A3818]/10 shadow-sm">Age/Sex</span>}
                                            {(!profile.height || !profile.weight) && <span className="text-[9px] bg-white text-[#1A3818]/60 px-2 py-1 rounded-lg border border-[#1A3818]/10 shadow-sm">Height/Weight</span>}
                                            {!profile.occupation && <span className="text-[9px] bg-white text-[#1A3818]/60 px-2 py-1 rounded-lg border border-[#1A3818]/10 shadow-sm">Occupation</span>}
                                            {!profile.primary_goal && <span className="text-[9px] bg-white text-[#1A3818]/60 px-2 py-1 rounded-lg border border-[#1A3818]/10 shadow-sm">Primary Goal</span>}
                                            {!profile.dietary_preference && <span className="text-[9px] bg-white text-[#1A3818]/60 px-2 py-1 rounded-lg border border-[#1A3818]/10 shadow-sm">Diet</span>}
                                            {(!profile.health_data?.medical_history || profile.health_data.medical_history.length === 0) && <span className="text-[9px] bg-white text-[#1A3818]/60 px-2 py-1 rounded-lg border border-[#1A3818]/10 shadow-sm">Medical History</span>}
                                            {(!profile.allergies || (profile.allergies as any)?.length === 0) && <span className="text-[9px] bg-white text-[#1A3818]/60 px-2 py-1 rounded-lg border border-[#1A3818]/10 shadow-sm">Allergies</span>}
                                            {(!profile.medications || (profile.medications as any)?.length === 0) && <span className="text-[9px] bg-white text-[#1A3818]/60 px-2 py-1 rounded-lg border border-[#1A3818]/10 shadow-sm">Medications</span>}
                                            {!profile.vikriti_dosha && <span className="text-[9px] bg-white text-[#1A3818]/60 px-2 py-1 rounded-lg border border-[#1A3818]/10 shadow-sm">Current State (S1)</span>}
                                            {!profile.prakriti_dosha && <span className="text-[9px] bg-white text-[#1A3818]/60 px-2 py-1 rounded-lg border border-[#1A3818]/10 shadow-sm">Lifelong Nature (S2)</span>}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Educational Link - Centered per screenshot */}
                            <div className="mt-8 flex justify-center">
                                <a 
                                    href="https://www.naturenani.com/learn/what-is-ayurveda" 
                                    target="_blank" 
                                    className="text-[#2D6A4F] text-[13px] font-medium flex items-center gap-1.5 hover:opacity-80 transition-opacity pb-0.5 border-b border-[#2D6A4F]/30"
                                >
                                    New to Ayurveda? Learn about Doshas and constitution →
                                </a>
                            </div>
                        </div>
                    </header>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 relative">
                        {/* Why It Matters */}
                        <div className="bg-white border border-gray-100 rounded-[1.2rem] p-8 shadow-sm hover:shadow-md transition-all">
                            <div className="flex items-center gap-3 mb-6">
                                <div className="w-2.5 h-2.5 rounded-full bg-[#1e4d38]" />
                                <h4 className="text-sm font-bold text-[#1A3818]">Why it matters</h4>
                            </div>
                            <p className="text-[13px] text-gray-600 leading-relaxed mb-6">
                                In Ayurveda, we look at both your lifelong nature (Prakriti) and your current state of imbalance (Vikriti). This helps us see the gap between who you are and how you feel.
                            </p>
                            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-4">Your Profile Enables Nani To</p>
                            <ul className="space-y-4">
                                {[
                                    "Refine herbal recommendations with precision",
                                    "Tailor daily rituals to your metabolic fire",
                                    "Fit meal plans to your body's seasonal needs"
                                ].map((point, i) => (
                                    <li key={i} className="text-[13px] text-gray-700 font-medium flex items-start gap-3">
                                        <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#1e4d38] shrink-0" />
                                        {point}
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {/* Current State Insights */}
                        {profile?.vikriti_dosha && (
                            <div className="bg-white border border-gray-100 rounded-[1.2rem] p-8 shadow-sm hover:shadow-md transition-all">
                                <div className="flex items-center gap-3 mb-6">
                                    <div className="w-2.5 h-2.5 rounded-full bg-[#B7791F]" />
                                    <h4 className="text-sm font-bold text-[#1A3818]">Current state insights</h4>
                                </div>
                                <p className="text-[13px] text-gray-600 leading-relaxed mb-8">
                                    Personalised signals detected from your body's current rhythm. Focus on these adjustments to realign with your natural constitution.
                                </p>
                                <ul className="space-y-4">
                                    {((vd: string | undefined): string[] => {
                                        if (!vd) return [];
                                        return vd.includes('vata') ? ['Digestive stabilisation needed', 'Energy peaks in afternoon', 'Routine is key'] :
                                               vd.includes('pitta') ? ['Cooling foods recommended', 'Avoid intensity mid-day', 'Hydration is critical'] :
                                               ['Morning activity preferred', 'Light, warm diet', 'Stimulate circulation'];
                                    })(profile.vikriti_dosha).map((point, i) => (
                                        <li key={i} className="text-[13px] text-gray-700 font-medium flex items-start gap-3">
                                            <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#B7791F] shrink-0" />
                                            {point}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>

                    {/* Snapshot Cards Section */}
                    {(journeyState === 'B' || journeyState === 'C') && (
                        <div className="space-y-6 pt-6 border-t border-gray-100">
                            <div className="flex items-center justify-between">
                                <h3 className="text-[11px] font-bold tracking-[0.2em] text-[#1A3818]/60 uppercase">
                                     Assessment Snapshots
                                 </h3>
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                {/* Card 1 — ESSENTIAL DETAILS · S0 */}
                                <SnapshotCard 
                                    title="ESSENTIAL DETAILS · S0"
                                    icon={<Leaf className="text-[#2D5A27]" size={18} />}
                                    color="[#2D5A27]"
                                    sections={[
                                        {
                                            label: "BASELINE METRICS",
                                            fields: {
                                                "Age": profile.age,
                                                "Biological Sex": profile.sex,
                                                "Height / Weight": (profile.height || profile.weight) ? `${profile.height || '—'} cm · ${profile.weight || '—'} kg` : null,
                                                "Occupation": profile.occupation,
                                                "Dietary Preference": profile.dietary_preference,
                                                "Known Allergies": profile.allergies,
                                                "Current Medications": profile.medications || profile.health_data?.medications
                                            }
                                        }
                                    ]}
                                />

                                {/* Card 2 — BASELINE NATURE · S2 */}
                                {onboardingProfile?.lifestyle_profile && (
                                    <SnapshotCard 
                                        title="BASELINE NATURE · S2"
                                        icon={<Leaf className="text-[#2D5A27]" size={18} />}
                                        color="[#2D5A27]"
                                        sections={[
                                            {
                                                label: "CONSTITUTIONAL ASSESSMENT",
                                                fields: {
                                                    "Energy Rhythm": onboardingProfile.lifestyle_profile.energy_pattern,
                                                    "Weather Sensitivity": onboardingProfile.lifestyle_profile.weather_sensitivity,
                                                    "Body Frame": onboardingProfile.lifestyle_profile.body_frame,
                                                    "Digestion": onboardingProfile.lifestyle_profile.natural_digestion,
                                                    "Skin & Hair": onboardingProfile.lifestyle_profile.skin_hair_type,
                                                    "Temperament": onboardingProfile.lifestyle_profile.natural_temperament
                                                }
                                            },
                                            // Women's Health fields if populated
                                            ...(onboardingProfile.life_stage || onboardingProfile.menstrual_pattern ? [{
                                                label: "WOMEN'S HEALTH",
                                                fields: {
                                                    "Life Stage": onboardingProfile.life_stage,
                                                    "Menstrual Pattern": onboardingProfile.menstrual_pattern,
                                                    "Hormonal Context": onboardingProfile.hormonal_context,
                                                    "Flow Color": onboardingProfile.flow_color
                                                }
                                            }] : [])
                                        ]}
                                    />
                                )}

                                {/* Card 3 — CURRENT STATE · S1 */}
                                {journeyState === 'C' && onboardingProfile?.lifestyle_profile && (
                                    <SnapshotCard 
                                        title="CURRENT STATE · S1"
                                        icon={<Activity className="text-[#B7791F]" size={18} />}
                                        color="[#B7791F]"
                                        sections={[
                                            {
                                                label: "CURRENT STATE ASSESSMENT",
                                                fields: {
                                                    "Onset": onboardingProfile.lifestyle_profile.onset || onboardingProfile.onset_timeframe,
                                                    "Life Events": onboardingProfile.lifestyle_profile.life_events,
                                                    "Affected Areas": onboardingProfile.affected_zones ? Object.values(onboardingProfile.affected_zones).map((z: any) => z.zone) : null,
                                                    "Meal Timing": onboardingProfile.lifestyle_profile.meal_timing || onboardingProfile.meal_regularity,
                                                    "Sleep Quality": onboardingProfile.lifestyle_profile.sleep_quality || onboardingProfile.sleep_pattern,
                                                    "Stress Level": onboardingProfile.lifestyle_profile.stress_intensity || onboardingProfile.stress_level,
                                                    "Emotional State": onboardingProfile.lifestyle_profile.stress_level
                                                }
                                            }
                                        ]}
                                    />
                                )}
                            </div>
                        </div>
                    )}

                    {/* Notice line withLinks */}
                    <div className="pt-10 pb-6 text-center border-t border-gray-50">
                        <div className="flex flex-col items-center gap-3 text-center mt-6 mb-8">
                            <p className="text-[11px] font-bold text-[#1A3818]/30 uppercase tracking-[0.2em]">
                                {onboardingProfile?.vikriti_last_updated 
                                    ? `Profile last updated on ${new Date(onboardingProfile.vikriti_last_updated).toLocaleDateString('en-US')}`
                                    : onboardingProfile?.completed_at 
                                        ? `Prakriti last updated on ${new Date(onboardingProfile.completed_at).toLocaleDateString('en-US')}`
                                        : "Assessment Pending"
                                }
                            </p>
                            <div className="flex items-center gap-6">
                                <button 
                                    onClick={() => navigate('/onboarding', { state: { mode: 'vikriti-refresh' } })}
                                    className="text-[13px] text-[#B7791F] font-bold hover:underline"
                                >
                                    Update state
                                </button>
                                <span className="text-[#1A3818]/10 h-3 w-px bg-current"></span>
                                <button 
                                    onClick={() => navigate('/onboarding', { state: { mode: 'prakriti-retake' } })}
                                    className="text-[13px] text-[#1A3818]/60 font-medium hover:underline"
                                >
                                    Retake complete wellness journey
                                </button>
                            </div>
                        </div>
                    </div>

                {/* Persistence Badge Logic */}
                {message && (
                    <div className={`fixed bottom-24 left-1/2 -translate-x-1/2 px-6 py-4 rounded-2xl shadow-2xl text-sm font-bold flex items-center gap-3 ${message.type === 'success' ? 'bg-[#2D5A27] text-[#F5F5DC]' : 'bg-red-100 text-red-800'} animate-in fade-in slide-in-from-bottom-6 z-50`}>
                        {message.type === 'success' && <Leaf size={16} className="text-yellow-400" />}
                        {message.text}
                    </div>
                )}
            </div >
        </div >
    );
};

export default WellnessPassport;
